import { useCallback, useEffect, RefObject } from 'react';

type UseScrollSyncOptions = {
    enabled?: boolean;
    throttle?: boolean;
};

/**
 * Хук для синхронизации скролла между textarea и overlay элементами
 * Обеспечивает синхронизацию scrollTop и scrollLeft между двумя элементами
 * @param sourceRef - реф на элемент-источник скролла (textarea)
 * @param targetRef - реф на элемент-цель синхронизации (overlay)
 * @param options - опции для настройки синхронизации
 */
export const useScrollSync = (
    sourceRef: RefObject<HTMLTextAreaElement | null>,
    targetRef: RefObject<HTMLDivElement | null>,
    options: UseScrollSyncOptions = {},
) => {
    const { enabled = true, throttle = true } = options;

    const syncScroll = useCallback(() => {
        const source = sourceRef.current;
        const target = targetRef.current;

        if (!source || !target || !enabled) {
            return;
        }

        // Синхронизируем позицию скролла
        target.scrollTop = source.scrollTop;
        target.scrollLeft = source.scrollLeft;
    }, [sourceRef, targetRef, enabled]);

    const handleScroll = useCallback(() => {
        if (throttle) {
            // Используем requestAnimationFrame для оптимизации производительности
            requestAnimationFrame(syncScroll);
        } else {
            syncScroll();
        }
    }, [syncScroll, throttle]);

    useEffect(() => {
        const source = sourceRef.current;

        if (!source || !enabled) {
            return;
        }

        // Добавляем обработчик события скролла
        source.addEventListener('scroll', handleScroll, { passive: true });

        // Выполняем начальную синхронизацию
        syncScroll();

        return () => {
            source.removeEventListener('scroll', handleScroll, { passive: true } as EventListenerOptions);
        };
    }, [sourceRef, handleScroll, syncScroll, enabled]);

    // Возвращаем функцию для ручной синхронизации
    return syncScroll;
};
